import { CUSTOM_LAYER_SOLID_COLOR, layerColor } from "@/lib/palette";
import { strategyLayerChoiceKey } from "@/lib/strategy/choice-key";
import type { StrategyLayer } from "@/types/strategy";

const STORAGE_KEY = "abg.strategyColorDefaults.v1";

function readDefaults(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, string>)
      : {};
  } catch {
    return {};
  }
}

function writeDefaults(defaults: Record<string, string>) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
}

export function getStrategyColorDefault(choiceKey: string): string | null {
  return readDefaults()[choiceKey] ?? null;
}

export function setStrategyColorDefault(choiceKey: string, color: string) {
  writeDefaults({ ...readDefaults(), [choiceKey]: color });
}

export function removeStrategyColorDefault(choiceKey: string) {
  const { [choiceKey]: _removed, ...rest } = readDefaults();
  writeDefaults(rest);
}

const SYSTEM_CHOICE_ORDER = [
  "custom",
  "long_call",
  "short_call",
  "long_put",
  "short_put",
  "covered_call",
  "cash_secured_put",
  "bull_call_spread",
  "bear_put_spread",
  "bull_put_spread",
  "bear_call_spread",
  "iron_condor",
  "iron_fly",
  "butterfly",
  "broken_wing_butterfly",
  "long_condor",
  "short_condor",
  "jade_lizard",
  "ratio_backspread",
  "ratio_spread",
  "bull_call_ladder",
  "bear_call_ladder",
  "bull_put_ladder",
  "bear_put_ladder",
  "synthetic_future",
  "combo",
  "straddle",
  "strangle",
  "strip",
  "strap",
  "guts",
  "time_spread",
  "double_time_spread",
] as const;

export function systemStrategyColor(choiceKey: string): string {
  if (choiceKey === "custom") {
    return CUSTOM_LAYER_SOLID_COLOR;
  }
  const index = SYSTEM_CHOICE_ORDER.findIndex((key) => key === choiceKey);
  return layerColor(index < 0 ? 0 : index - 1);
}

export function applyChoiceColorToLayers(
  layers: readonly unknown[],
  choiceKey: string,
  color: string,
): { changed: boolean; layers: StrategyLayer[] } {
  let changed = false;
  const next = layers.map((raw) => {
    const layer = raw as StrategyLayer;
    if (
      !layer ||
      typeof layer !== "object" ||
      !("kind" in layer) ||
      !("params" in layer)
    ) {
      return layer;
    }
    if (strategyLayerChoiceKey(layer) !== choiceKey) return layer;
    if (layer.color === color) return layer;
    changed = true;
    return { ...layer, color };
  });
  return { changed, layers: next };
}
